/**
 * What a saved chart is showing, in words.
 *
 * A tile renders a picture and a title, and nothing else says whether the
 * numbers are counts or percentages, what they are broken down by, or which
 * window they cover. Two pins of the same question with different settings
 * therefore look identical — which they now can be, since a question may be
 * pinned more than once as long as the configuration differs.
 *
 * Derived from the stored spec rather than from whatever the builder happened
 * to have on screen, so it describes what was actually run.
 */
import { chartedFieldOf, isScoredMeasure } from './chartOverrides.js';
import { DATE_RANGE_PRESET_LABELS } from './dateRange.js';
/**
 * English, translated at the point of use — the convention elsewhere in this
 * codebase, and it keeps this module free of an i18n dependency.
 */
const MEASURE_LABELS = {
    responseCount: 'Responses',
    respondentPercent: '% of respondents',
    selectionCount: 'Selections',
    distinctEmployees: 'People',
    topBox: 'Agree or better',
    bottomBox: 'Disagree or worse',
    mean: 'Average score',
};
const describePeriod = (range) => {
    if (!range)
        return { kind: 'label', label: 'All time' };
    switch (range.mode) {
        case 'allTime':
            return { kind: 'label', label: 'All time' };
        case 'rolling':
            // Not in the preset list: the builder offers 30 and 90 days, and a
            // stored spec can carry any number.
            return { kind: 'label', label: `Last ${range.lastDays} days` };
        case 'preset':
            return {
                kind: 'label',
                label: DATE_RANGE_PRESET_LABELS[range.preset] ??
                    range.preset,
            };
        case 'absolute':
            return {
                kind: 'range',
                startDate: range.startDate,
                endDate: range.endDate,
            };
        default:
            return { kind: 'label', label: 'All time' };
    }
};
/**
 * The dimension that is *not* the question being charted.
 *
 * A scored measure puts its breakdown in `rows` because the question has
 * become the measure; a distribution charts the question in `rows` and puts
 * the breakdown in `columns`. Reading the wrong one would report a chart as
 * broken down by the very question it is about.
 */
const breakdownLabelOf = (spec) => {
    // Optional chaining on purpose: this feeds a caption, and a caption must
    // never be the reason a tile fails to render. A spec missing its dimensions
    // is describable as "no breakdown".
    const dimension = isScoredMeasure(spec.measure)
        ? spec.rows?.[0]
        : spec.columns?.[0];
    const label = dimension?.field?.label;
    return label ?? null;
};
export const summariseChartSpec = (spec) => {
    if (!spec?.measure)
        return null;
    return {
        measure: MEASURE_LABELS[spec.measure.kind] ?? spec.measure.kind,
        breakdown: breakdownLabelOf(spec),
        period: describePeriod(spec.dateRange),
    };
};
/** The question a chart is about, for a tooltip or a fallback title. */
export const chartedQuestionLabel = (spec) => {
    if (!spec)
        return null;
    const field = chartedFieldOf(spec);
    return field?.label ?? null;
};
